import React, { ReactElement, createContext, useContext, useState, useCallback, useMemo } from 'react';

import * as RadixAccordion from '@radix-ui/react-accordion';
import type { AccordionItemProps as RadixAccordionItemProps } from '@radix-ui/react-accordion';
import noop from 'lodash/noop';
import { twMerge } from 'tailwind-merge';

interface WizardContextModel {
  activeStep: string | number;
  nextStep: () => void;
  prevStep: () => void;
}

interface WizardProviderModel {
  defaultStep: string | number;
  children: ReactElement;
}

interface WizardContainerProps {
  readonly children: (args: WizardContextModel) => ReactElement;
}

interface WizardRootProps extends WizardContainerProps, Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  readonly defaultStep: number | string;
}

interface WizardItemProps extends React.HTMLAttributes<HTMLDivElement>, Omit<RadixAccordionItemProps, 'value'> {
  readonly step: string | number;
}

const WizardContext = createContext<WizardContextModel>({
  activeStep: 1,
  nextStep: noop,
  prevStep: noop,
});

function WizardProvider({ defaultStep, children }: WizardProviderModel) {
  const [activeStep, setActiveStep] = useState(Number(defaultStep));
  const nextStep = useCallback(() => setActiveStep((prevActiveStep: number) => prevActiveStep + 1), []);
  const prevStep = useCallback(() => setActiveStep((prevActiveStep: number) => prevActiveStep - 1), []);
  const value = useMemo(() => ({ activeStep, nextStep, prevStep }), [activeStep, nextStep, prevStep]);

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

const useWizard = () => useContext(WizardContext);

function WizardContainer({ children }: WizardContainerProps) {
  const state = useWizard();

  return (
    <RadixAccordion.Root type="single" value={String(state.activeStep)} className={twMerge('grid gap-2')}>
      {children(state)}
    </RadixAccordion.Root>
  );
}

function Wizard({ defaultStep, children }: WizardRootProps) {
  return (
    <WizardProvider defaultStep={defaultStep}>
      <WizardContainer>{children}</WizardContainer>
    </WizardProvider>
  );
}

function WizardItem({ step, children, className }: WizardItemProps) {
  const { activeStep } = useWizard();
  const classes = twMerge(
    'group rounded-lg border border-neutral-300 bg-white dark:border-neutral-700 dark:bg-neutral-900 dark:text-white',
    activeStep === Number(step) && 'is-open',
    className,
  );

  return (
    <RadixAccordion.Item value={String(step)} className={classes}>
      {children}
    </RadixAccordion.Item>
  );
}

function WizardHeader({ children, className }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <RadixAccordion.Header
      className={twMerge(
        'flex grow-0 items-center justify-between px-4 py-3 text-sm font-bold group-[.is-open]:border-b dark:group-[.is-open]:border-b-neutral-500',
        className,
      )}
    >
      <RadixAccordion.Trigger disabled>{children}</RadixAccordion.Trigger>
    </RadixAccordion.Header>
  );
}

function WizardContent({ children, className }: React.HTMLAttributes<HTMLDivElement>) {
  return <RadixAccordion.Content className={twMerge('hidden p-4 group-[.is-open]:block', className)}>{children}</RadixAccordion.Content>;
}

Wizard.Item = WizardItem;
Wizard.Header = WizardHeader;
Wizard.Content = WizardContent;

export { Wizard, useWizard };
